import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { EXTERNAL_SHARE } from '@confluence/named-routes/entry-points/routes.ts';

import { useWithUserTypes } from './useWithUserTypes.ts';
import { ExternalShareContext } from './ExternalShareContext.tsx';

type ExternalShareContextProviderProps = {
	path: string;
	children?: ReactNode;
	shareToken?: string;
};

export const ExternalShareContextProvider: FC<ExternalShareContextProviderProps> = ({
	children,
	path,
	shareToken,
}) => {
	const isExternalShareRequest = !!EXTERNAL_SHARE.match(path);
	const { userType } = useWithUserTypes(isExternalShareRequest);

	// Extract shareToken from path if it's an external share request
	const extractedShareToken = useMemo(() => {
		if (isExternalShareRequest) {
			const match = EXTERNAL_SHARE.match(path);
			return match?.params?.externalToken || undefined;
		}
		return undefined;
	}, [isExternalShareRequest, path]);

	// Use provided shareToken if available, otherwise use extracted one
	const resolvedShareToken = shareToken || extractedShareToken;

	const externalShareContextValue = useMemo(
		() => ({
			isExternalShareRequest,
			userType,
			shareToken: resolvedShareToken,
		}),
		[isExternalShareRequest, userType, resolvedShareToken],
	);

	return (
		<ExternalShareContext.Provider value={externalShareContextValue}>
			{children}
		</ExternalShareContext.Provider>
	);
};
